import { ACTIVE_LOCATION_STORAGE_KEY, OFFLINE_HEADERS } from '@jq4race/shared';
import { ApiError } from './errors.js';
import { handleLocationAccessDenied } from './location-access-interceptor.js';
const PROTECTED_HEADER_NAMES = new Set(['content-type', 'authorization', 'cookie']);
/**
 * Merges caller-supplied headers underneath this function's own
 * content-type/authorization — case-insensitively, so a caller can never
 * sneak a differently-cased `Authorization`/`Cookie`/`Content-Type` key past
 * the protection (second adversarial review finding #8).
 */
function buildHeaders(callerHeaders) {
    const headers = {};
    for (const [key, value] of Object.entries(callerHeaders ?? {})) {
        if (PROTECTED_HEADER_NAMES.has(key.toLowerCase()))
            continue;
        headers[key] = value;
    }
    headers['content-type'] = 'application/json';
    return headers;
}
/** Case-insensitive "is this header name already present" check. */
function hasHeader(headers, name) {
    const lower = name.toLowerCase();
    return Object.keys(headers).some((k) => k.toLowerCase() === lower);
}
/** Case-insensitive lookup of the ACTUAL key as the caller cased it, or undefined. */
function findHeaderKey(headers, name) {
    const lower = name.toLowerCase();
    return Object.keys(headers).find((k) => k.toLowerCase() === lower);
}
let baseUrl = 'http://localhost:3000';
// Base URL for /auth/* endpoints. Kept separate from `baseUrl` so the
// httpOnly `jq4_refresh` cookie can be made first-party in production: when
// frontend and backend live on cross-site domains (e.g. *.up.railway.app, a
// public suffix), the browser drops the third-party refresh cookie and every
// reload/bootstrap loses the session. Pointing auth calls at a same-origin
// path (proxied to the backend) makes the cookie first-party. `null` → fall
// back to `baseUrl` (default; unchanged behavior for existing callers).
let authBaseUrl = null;
let getAccessToken = () => null;
let triggerRefresh = async () => null;
let onSessionExpired = () => { };
export function configureClient(opts) {
    if (opts.baseUrl)
        baseUrl = opts.baseUrl;
    // '' is a valid value (same-origin); absence resets to null (fall back to
    // baseUrl) so each configureClient call is fully deterministic.
    authBaseUrl = opts.authBaseUrl ?? null;
    getAccessToken = opts.getAccessToken;
    triggerRefresh = opts.refresh;
    onSessionExpired = opts.onSessionExpired;
}
/** Returns the currently configured base URL (useful for low-level fetch helpers). */
export function getBaseUrl() {
    return baseUrl;
}
/** Returns the currently configured access token (useful for low-level fetch helpers). */
export function getCurrentAccessToken() {
    return getAccessToken();
}
// ---------------------------------------------------------------------------
// X-Location-Id header injection
// ---------------------------------------------------------------------------
/**
 * Paths that must NOT receive the X-Location-Id header. These endpoints use
 * :locationId as a path param (assignment management) or return location lists
 * (me/locations) — injecting the header there is meaningless and would confuse
 * any future guard.
 */
const SKIP_LOCATION_HEADER_PATTERNS = [
    /^\/admin\/locations\/[^/]+\/assignments(\/.*)?$/,
    /^\/admin\/users\/[^/]+\/locations$/,
    /^\/me\/locations$/,
];
/** @internal — exported for contract tests only */
export function shouldInjectLocationHeader(path) {
    return !SKIP_LOCATION_HEADER_PATTERNS.some((re) => re.test(path));
}
export async function request(path, opts = {}) {
    // Caller-supplied headers are merged first, with content-type/authorization
    // (case-insensitively) stripped from them — this function is always the
    // sole author of those two, so a caller can never override the auth header
    // this way, in any casing (second adversarial review finding #8).
    const headers = buildHeaders(opts.headers);
    const token = getAccessToken();
    if (token)
        headers.authorization = `Bearer ${token}`;
    // /auth/* endpoints go through authBaseUrl when configured so the refresh
    // cookie stays first-party; everything else hits the data API base. The
    // trailing slash keeps the match tight (won't catch a future /authors etc.).
    const effectiveBase = path.startsWith('/auth/') && authBaseUrl !== null ? authBaseUrl : baseUrl;
    // Inject active location header for operational endpoints (FR-20) — but
    // ONLY when the caller did not already supply one (second adversarial
    // review finding #2: a background drainer sync must send the ITEM's own
    // captured `X-Location-Id`, which must win over whatever the operator's
    // CURRENT foreground location happens to be in localStorage).
    if (shouldInjectLocationHeader(path) &&
        !hasHeader(headers, 'x-location-id') &&
        typeof localStorage !== 'undefined') {
        const activeLocationId = localStorage.getItem(ACTIVE_LOCATION_STORAGE_KEY);
        if (activeLocationId) {
            headers['X-Location-Id'] = activeLocationId;
        }
    }
    const init = {
        method: opts.method ?? 'GET',
        headers,
        credentials: 'include',
        signal: opts.signal,
    };
    if (opts.body !== undefined)
        init.body = JSON.stringify(opts.body);
    let res;
    try {
        res = await fetch(`${effectiveBase}${path}`, init);
    }
    catch (err) {
        throw new ApiError('network_error', err instanceof Error ? err.message : 'fetch failed');
    }
    if (res.status === 401 && opts.refreshOn401 !== false && path !== '/auth/refresh') {
        const newToken = await triggerRefresh().catch(() => null);
        if (newToken) {
            // pwa-offline-first Slice 7 (deferred finding from Slice 6 round 5,
            // LOW #5) — a caller carrying the offline replay headers (the sync
            // drainer, via checkin-create.handler.ts) computes X-Client-Sent-At
            // ONCE, right before its own call. If THIS attempt 401s, the refresh
            // round-trip above can take a meaningful amount of wall-clock time —
            // reusing that now-stale value on the retry would corrupt the
            // server's clock-skew correction (design D15: `corrected = capturedAt
            // + (serverNow - clientSentAt)`). Recompute it fresh for the retry
            // only; X-Captured-At (the actual capture instant) is untouched.
            let retryHeaders = opts.headers;
            if (retryHeaders) {
                const clientSentAtKey = findHeaderKey(retryHeaders, OFFLINE_HEADERS.clientSentAt);
                if (clientSentAtKey) {
                    retryHeaders = { ...retryHeaders, [clientSentAtKey]: new Date().toISOString() };
                }
            }
            return request(path, { ...opts, headers: retryHeaders, refreshOn401: false });
        }
        onSessionExpired();
    }
    if (res.status === 204)
        return undefined;
    let payload = null;
    try {
        payload = await res.json();
    }
    catch {
        payload = null;
    }
    if (!res.ok) {
        const serverCode = payload?.code;
        const code = serverCode ?? mapStatusToCode(res.status);
        const message = payload?.message ?? `HTTP ${res.status}`;
        // 403 with location-denial codes: clear localStorage + dispatch event
        // (FR-17, WU-24) — SUPPRESSED for background-drain requests (second
        // adversarial review finding #4): the drainer must never yank the
        // operator's current foreground location out from under them just
        // because one queued item's own (possibly stale) location was denied.
        if (res.status === 403 && !opts.suppressLocationAccessHandling) {
            handleLocationAccessDenied(String(code));
        }
        throw new ApiError(code, message, res.status, payload, serverCode === undefined);
    }
    return payload;
}
function mapStatusToCode(status) {
    if (status === 401)
        return 'invalid_credentials';
    if (status === 409)
        return 'refresh_in_progress';
    if (status === 423)
        return 'account_locked';
    if (status === 429)
        return 'rate_limited';
    return 'unknown';
}
//# sourceMappingURL=client.js.map